import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import getQueryKeys from "common/datahooks/getQueryKeys";
import { useCampaignContext } from "common/helpers/CampaignContext";
import { del, patch, post } from "common/helpers/HTTP";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";

import { useCampaignFlowContext } from "../context/CampaignFlowContext";
import useUpdateCampaign from "./useUpdateCampaign";

import type { CampaignFlow, FlowMessageTemplate } from "../types";

async function addFlowMessageRequest(
  workspaceId: string,
  campaignId: string,
  flowId: string,
  flowMessage: FlowMessageTemplate,
): Promise<Omit<CampaignFlow, "metrics">> {
  return (
    await post(
      `workspaces/${workspaceId}/campaigns/${campaignId}/flows/${flowId}/messages`,
      flowMessage,
    )
  ).flow;
}
async function deleteLastFlowMessageRequest(
  workspaceId: string,
  campaignId: string,
  flowId: string,
) {
  await del(
    `workspaces/${workspaceId}/campaigns/${campaignId}/flows/${flowId}/messages`,
  );
}

async function updateFlowMessageRequest(
  workspaceId: string,
  campaignId: string,
  flowId: string,
  index: number,
  updates: Partial<FlowMessageTemplate>,
): Promise<CampaignFlow> {
  return (
    await patch(
      `workspaces/${workspaceId}/campaigns/${campaignId}/flows/${flowId}/messages/${index}`,
      updates,
    )
  ).flow;
}

export default function useFlowMessages() {
  const queryClient = useQueryClient();
  const { id: campaignId } = useCampaignContext();
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const { campaignsKeys } = getQueryKeys(workspaceId);
  const { flow: campaignFlow } = useCampaignFlowContext();
  const { updateCachedCampaign } = useUpdateCampaign();

  const { mutateAsync: addFlowMessage, isPending: isAddingFlowMessage } =
    useMutation({
      mutationFn: ({ flowMessage }: { flowMessage: FlowMessageTemplate }) =>
        addFlowMessageRequest(
          workspaceId,
          campaignId,
          campaignFlow.id,
          flowMessage,
        ),
      onSuccess: (flow) =>
        updateCachedCampaign(campaignId, (draftCampaign) => {
          const flowIndex = draftCampaign.flows.findIndex(
            (draftFlow) => draftFlow.id === flow.id,
          );

          draftCampaign.flows[flowIndex] = {
            ...draftCampaign.flows[flowIndex],
            flow_message_templates: flow.flow_message_templates,
          };
        }),
    });

  const {
    mutateAsync: deleteLastFlowMessage,
    isPending: isDeletingLastFlowMessage,
  } = useMutation({
    mutationFn: () =>
      deleteLastFlowMessageRequest(workspaceId, campaignId, campaignFlow.id),
    onSuccess: () =>
      updateCachedCampaign(campaignId, (draftCampaign) => {
        const flowIndex = draftCampaign.flows.findIndex(
          (draftFlows) => draftFlows.id === campaignFlow.id,
        );

        draftCampaign.flows[flowIndex].flow_message_templates.pop();
      }),
  });

  const { mutateAsync: updateFlowMessage, isPending: isUpdatingFlowMessage } =
    useMutation({
      mutationFn: ({
        index,
        updates,
      }: {
        index: number;
        updates: Partial<FlowMessageTemplate>;
      }) =>
        updateFlowMessageRequest(
          workspaceId,
          campaignId,
          campaignFlow.id,
          index,
          updates,
        ),
      onMutate: async ({ index, updates }) => {
        await queryClient.cancelQueries({
          queryKey: campaignsKeys.details(campaignId),
        });

        const previousCampaign = queryClient.getQueryData(
          campaignsKeys.details(campaignId),
        );

        updateCachedCampaign(campaignId, (draftCampaign) => {
          const flowIndex = draftCampaign.flows.findIndex(
            (flow) => flow.id === campaignFlow.id,
          );

          draftCampaign.flows[flowIndex].flow_message_templates[index] = {
            ...draftCampaign.flows[flowIndex].flow_message_templates[index],
            ...updates,
          };
        });

        return { previousCampaign };
      },
      onError: (_, { index }, { previousCampaign }) => {
        queryClient.setQueryData(
          campaignsKeys.details(campaignId),
          previousCampaign,
        );

        toast.error(`Error updating flow message #${index}`);
      },
    });

  function addPlaceholderMessage() {
    updateCachedCampaign(campaignId, (draftCampaign) => {
      const flowIndex = draftCampaign.flows.findIndex(
        (flow) => flow.id === campaignFlow.id,
      );

      draftCampaign.flows[flowIndex].flow_message_templates.push({
        delay: 0,
        edited: false,
        message: "",
        original_id: null,
        type: "MESSAGE_TEMPLATE",
        ai_descriptors: {},
      });
    });
  }

  function removePlaceholderMessage() {
    updateCachedCampaign(campaignId, (draftCampaign) => {
      const flowIndex = draftCampaign.flows.findIndex(
        (flow) => flow.id === campaignFlow.id,
      );

      draftCampaign.flows[flowIndex].flow_message_templates.pop();
    });
  }

  return {
    addFlowMessage,
    isAddingFlowMessage,
    deleteLastFlowMessage,
    isDeletingLastFlowMessage,
    updateFlowMessage,
    isUpdatingFlowMessage,
    // Placeholder actions
    addPlaceholderMessage,
    removePlaceholderMessage,
  };
}
